// S6 — a tenant's entitlements (spec 15 §4; M15-6, D-486, D-615). The
// effective matrix with three columns per row (the plan's value, the
// override, the effective value), usage beside each limit where its owner
// answers it, an override dialog (reason required, optional expiry) and the
// plan move with the downgrade guard's warning. Every role reads; only
// platform_admin is offered a write, and a sandbox member on a fleet tenant
// is offered none (D-477).

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { CheckboxField, Field } from "../../../components/field";
import { Modal } from "../../../components/modal";
import { Select } from "../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../components/states";
import { StatusChip } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { TenantTabs } from "../components/tenant-tabs";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import { packLabel } from "../lib/labels";
import {
  getTenant,
  listVerticalPacks,
  PlatformError,
  type PlatformMe,
  type TenantDetail,
} from "../lib/platform-api";
import {
  getEntitlements,
  listPlans,
  removeOverride,
  setOverride,
  updateTenant,
  type LimitEntitlement,
  type ModuleEntitlement,
  type TenantEntitlements,
} from "../lib/plans-api";
import {
  assignablePlans,
  canEditTenant,
  formatBytes,
  formatCount,
  moduleLabel,
  planFailure,
} from "../lib/plans";
import { tenantQueryKey } from "./tenant-detail";

export function entitlementsQueryKey(id: string) {
  return ["platform", "tenant", id, "entitlements"] as const;
}

type OverrideTarget =
  { kind: "module"; row: ModuleEntitlement } | { kind: "limit"; row: LimitEntitlement };

function limitValue(n: number | null, t: TFunction, language: string): string {
  return n === null ? t("admin.entitlements.noLimit") : formatCount(n, language);
}

function usageText(row: LimitEntitlement, t: TFunction, language: string): string {
  if (row.usage === null) return t("admin.entitlements.noUsage");
  const used =
    row.usage.unit === "bytes"
      ? formatBytes(row.usage.value, language)
      : formatCount(row.usage.value, language);
  if (row.effective === null) return used;
  const limit =
    row.code === "storage_gb"
      ? formatBytes(row.effective * 1024 ** 3, language)
      : formatCount(row.effective, language);
  return t("admin.entitlements.usedOf", { used, limit });
}

export function TenantEntitlementsScreen({ me, tenantId }: { me: PlatformMe; tenantId: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const detail = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: ({ signal }) => getTenant(tenantId, signal),
  });
  const matrix = useQuery({
    queryKey: entitlementsQueryKey(tenantId),
    queryFn: ({ signal }) => getEntitlements(tenantId, signal),
  });
  const packs = useQuery({
    queryKey: ["platform", "vertical-packs"],
    queryFn: ({ signal }) => listVerticalPacks(signal),
    staleTime: 5 * 60_000,
  });
  useSignInAgainOn401(detail.error ?? matrix.error);
  const [target, setTarget] = useState<OverrideTarget | null>(null);
  const [planDialog, setPlanDialog] = useState(false);

  const tenant = detail.data?.tenant;
  const fleetOnly = me.scope === "sandbox" && tenant !== undefined && !tenant.sandbox;
  const editable = canEditTenant(me.role) && !fleetOnly;
  const packName = (key: string) => packLabel(key, packs.data, i18n.language);

  const crumbs = [{ label: t("admin.tenant.back"), to: "/console/tenants" }];
  if (detail.isPending || matrix.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-entitlements-loading">
        <PageHeader title={t("admin.entitlements.loading")} crumbs={crumbs} />
        <CardSkeleton lines={8} label={t("admin.entitlements.loading")} />
      </section>
    );
  }
  if (detail.isError || matrix.isError) {
    const error = detail.error ?? matrix.error;
    const notFound =
      error instanceof PlatformError && (error.status === 404 || error.status === 400);
    return (
      <section className="admin-screen" data-testid="admin-entitlements-error">
        <PageHeader
          title={notFound ? t("admin.tenant.notFoundTitle") : t("admin.load.failed")}
          crumbs={crumbs}
        />
        {notFound ? (
          <EmptyState
            title={t("admin.tenant.notFoundTitle")}
            body={t("admin.tenant.notFoundBody")}
            ctaLabel={t("admin.tenant.back")}
            onCta={() => {
              void navigate({ to: "/console/tenants" });
            }}
          />
        ) : (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
              void matrix.refetch();
            }}
            requestId={requestIdOf(error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        )}
      </section>
    );
  }

  const m = matrix.data;
  const overrideCell = (o: ModuleEntitlement["override"], text: string) =>
    o === null ? (
      <span className="admin-muted">{t("admin.entitlements.none")}</span>
    ) : (
      <span className="admin-stack">
        <span>{text}</span>
        <span className="admin-muted" title={o.reason}>
          {o.expires_at !== null
            ? t("admin.entitlements.until", {
                date: formatTimestamp(o.expires_at, i18n.language),
              })
            : t("admin.entitlements.noExpiry")}
        </span>
      </span>
    );

  const moduleColumns: DataTableColumn<ModuleEntitlement>[] = [
    {
      key: "module",
      header: t("admin.entitlements.moduleCol"),
      render: (row) => moduleLabel(row.code, t, packName),
    },
    {
      key: "plan",
      header: t("admin.entitlements.planCol"),
      render: (row) => (row.plan ? t("admin.entitlements.on") : t("admin.entitlements.off")),
    },
    {
      key: "override",
      header: t("admin.entitlements.overrideCol"),
      render: (row) =>
        overrideCell(
          row.override,
          row.override?.enabled === true ? t("admin.entitlements.on") : t("admin.entitlements.off"),
        ),
    },
    {
      key: "effective",
      header: t("admin.entitlements.effectiveCol"),
      render: (row) => (
        <StatusChip variant={row.effective ? "ok" : "neutral"} data-testid="admin-ent-effective">
          {row.effective ? t("admin.entitlements.on") : t("admin.entitlements.off")}
        </StatusChip>
      ),
    },
    ...(editable
      ? [
          {
            key: "act",
            header: t("admin.entitlements.actionsCol"),
            render: (row: ModuleEntitlement) => (
              <Button
                size="sm"
                data-testid="admin-ent-override"
                onClick={() => {
                  setTarget({ kind: "module", row });
                }}
              >
                {row.override === null
                  ? t("admin.entitlements.overrideAdd")
                  : t("admin.entitlements.overrideEdit")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  const limitColumns: DataTableColumn<LimitEntitlement>[] = [
    {
      key: "limit",
      header: t("admin.entitlements.limitCol"),
      render: (row) => (
        <span className="admin-stack">
          <span>{t(`admin.limitCodes.${row.code}`)}</span>
          <span className="admin-muted">{t(`admin.enforcement.${row.enforcement}`)}</span>
        </span>
      ),
    },
    {
      key: "plan",
      header: t("admin.entitlements.planCol"),
      render: (row) => limitValue(row.plan, t, i18n.language),
    },
    {
      key: "override",
      header: t("admin.entitlements.overrideCol"),
      render: (row) =>
        overrideCell(row.override, limitValue(row.override?.limit_value ?? null, t, i18n.language)),
    },
    {
      key: "effective",
      header: t("admin.entitlements.effectiveCol"),
      render: (row) => (
        <span className="admin-strong" data-testid="admin-ent-limit-effective">
          {limitValue(row.effective, t, i18n.language)}
        </span>
      ),
    },
    {
      key: "usage",
      header: t("admin.entitlements.usageCol"),
      render: (row) => (
        <span data-testid="admin-ent-usage">{usageText(row, t, i18n.language)}</span>
      ),
    },
    ...(editable
      ? [
          {
            key: "act",
            header: t("admin.entitlements.actionsCol"),
            render: (row: LimitEntitlement) => (
              <Button
                size="sm"
                data-testid="admin-ent-override"
                onClick={() => {
                  setTarget({ kind: "limit", row });
                }}
              >
                {row.override === null
                  ? t("admin.entitlements.overrideAdd")
                  : t("admin.entitlements.overrideEdit")}
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <section className="admin-screen" data-testid="admin-entitlements">
      <PageHeader title={detail.data.tenant.name} crumbs={crumbs} />
      <TenantTabs tenantId={tenantId} />
      {fleetOnly ? (
        <Banner kind="neutral" data-testid="admin-tenant-sandbox-only">
          {t("admin.tenant.sandboxOnly")}
        </Banner>
      ) : null}
      <Card title={t("admin.entitlements.plan")} data-testid="admin-ent-plan">
        <Card.Body>
          <div className="admin-inline">
            <span className="admin-strong" data-testid="admin-ent-plan-name">
              {m.plan.name}
            </span>
            <code>{m.plan.code}</code>
            {m.plan.status !== "active" ? (
              <StatusChip variant="neutral">{t(`admin.plans.status.${m.plan.status}`)}</StatusChip>
            ) : null}
            {editable ? (
              <Button
                size="sm"
                data-testid="admin-ent-change-plan"
                onClick={() => {
                  setPlanDialog(true);
                }}
              >
                {t("admin.entitlements.changePlan")}
              </Button>
            ) : null}
          </div>
        </Card.Body>
      </Card>
      <Card title={t("admin.entitlements.modules")} data-testid="admin-ent-modules">
        <DataTable
          columns={moduleColumns}
          rows={m.modules}
          rowKey={(row) => row.code}
          empty={null}
          caption={t("admin.entitlements.modules")}
          rowTestId="admin-ent-module-row"
          rowProps={(row) => ({ "data-code": row.code })}
        />
      </Card>
      <Card title={t("admin.entitlements.limits")} data-testid="admin-ent-limits">
        <DataTable
          columns={limitColumns}
          rows={m.limits}
          rowKey={(row) => row.code}
          empty={null}
          caption={t("admin.entitlements.limits")}
          rowTestId="admin-ent-limit-row"
          rowProps={(row) => ({ "data-code": row.code })}
        />
      </Card>
      {target !== null ? (
        <OverrideDialog
          tenantId={tenantId}
          target={target}
          label={
            target.kind === "module"
              ? moduleLabel(target.row.code, t, packName)
              : t(`admin.limitCodes.${target.row.code}`)
          }
          onClose={() => {
            setTarget(null);
          }}
        />
      ) : null}
      {planDialog ? (
        <PlanDialog
          detail={detail.data}
          entitlements={m}
          onClose={() => {
            setPlanDialog(false);
          }}
        />
      ) : null}
    </section>
  );
}

function OverrideDialog({
  tenantId,
  target,
  label,
  onClose,
}: {
  tenantId: string;
  target: OverrideTarget;
  label: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const saveKey = useHeldKey();
  const removeKey = useHeldKey();
  const existing = target.row.override;
  const [enabled, setEnabled] = useState(
    target.kind === "module" ? (existing?.enabled ?? !target.row.plan) : true,
  );
  const [value, setValue] = useState(
    target.kind === "limit" ? String(existing?.limit_value ?? target.row.effective ?? "") : "",
  );
  const [reason, setReason] = useState("");
  const [expires, setExpires] = useState("");
  const reasonOk = reason.trim().length >= 5;
  const valueOk = target.kind === "module" || /^\d+$/.test(value);
  const expiresIso = expires === "" ? undefined : new Date(`${expires}T23:59:59Z`).toISOString();

  function settle(next: TenantEntitlements) {
    queryClient.setQueryData(entitlementsQueryKey(tenantId), next);
    onClose();
  }
  const save = useMutation({
    mutationFn: () => {
      const body = {
        ...(target.kind === "module" ? { enabled } : { limit_value: Number(value) }),
        reason: reason.trim(),
        ...(expiresIso !== undefined ? { expires_at: expiresIso } : {}),
      };
      return withHeldKey(saveKey, { set: target.row.code, body }, (key) =>
        setOverride(tenantId, target.kind, target.row.code, body, key),
      );
    },
    onSuccess: settle,
  });
  const remove = useMutation({
    mutationFn: () =>
      withHeldKey(removeKey, { remove: target.row.code, reason: reason.trim() }, (key) =>
        removeOverride(tenantId, target.kind, target.row.code, reason.trim(), key),
      ),
    onSuccess: settle,
  });
  useSignInAgainOn401(save.error ?? remove.error);
  const title = t("admin.entitlements.overrideTitle", { name: label });
  const busy = save.isPending || remove.isPending;
  const error = save.error ?? remove.error;
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.plans.close")}
      onClose={onClose}
      pending={busy}
      testId="admin-override-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (reasonOk && valueOk) save.mutate();
        }}
      >
        {target.kind === "module" ? (
          <CheckboxField
            label={t("admin.entitlements.switchOn")}
            checked={enabled}
            onChange={setEnabled}
            data-testid="admin-override-enabled"
          />
        ) : (
          <Field label={t("admin.entitlements.value")}>
            <input
              className="ds-input"
              inputMode="numeric"
              value={value}
              data-testid="admin-override-value"
              onChange={(e) => {
                setValue(e.target.value);
              }}
            />
          </Field>
        )}
        <Field label={t("admin.entitlements.reason")} wide>
          <textarea
            className="ds-input"
            rows={2}
            maxLength={2000}
            value={reason}
            data-testid="admin-override-reason"
            onChange={(e) => {
              setReason(e.target.value);
            }}
          />
        </Field>
        <p className="admin-hint">{t("admin.entitlements.reasonHint")}</p>
        <Field label={t("admin.entitlements.expires")}>
          <input
            className="ds-input"
            type="date"
            value={expires}
            data-testid="admin-override-expires"
            onChange={(e) => {
              setExpires(e.target.value);
            }}
          />
        </Field>
        <p className="admin-hint">{t("admin.entitlements.expiresHint")}</p>
        {error !== null ? (
          <Banner kind="bad" data-testid="admin-override-error">
            {planFailure(error) === "invalid"
              ? t("admin.entitlements.invalid")
              : planFailure(error) === "role" || planFailure(error) === "scope"
                ? t("admin.tenant.refusedRole")
                : t("admin.entitlements.failed")}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={!reasonOk || !valueOk}
            busy={save.isPending}
            busyLabel={t("admin.plans.saving")}
            data-testid="admin-override-save"
          >
            {t("admin.entitlements.save")}
          </Button>
          {existing !== null ? (
            <Button
              variant="danger"
              disabled={!reasonOk || busy}
              busy={remove.isPending}
              busyLabel={t("admin.entitlements.removing")}
              data-testid="admin-override-remove"
              onClick={() => {
                remove.mutate();
              }}
            >
              {t("admin.entitlements.remove")}
            </Button>
          ) : null}
          <Button disabled={busy} onClick={onClose}>
            {t("admin.plans.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function PlanDialog({
  detail,
  entitlements,
  onClose,
}: {
  detail: TenantDetail;
  entitlements: TenantEntitlements;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const heldKey = useHeldKey();
  const plans = useQuery({
    queryKey: ["platform", "plans", { showRetired: false }],
    queryFn: ({ signal }) => listPlans(false, signal),
  });
  const tenant = detail.tenant;
  const choices = plans.data
    ? assignablePlans(plans.data, {
        sandboxTenant: tenant.sandbox,
        // A seeded tenant has no recorded pack; the server then checks none.
        pack: tenant.group_admin_email === "" ? null : tenant.vertical_pack,
      }).filter((p) => p.id !== entitlements.plan.id)
    : [];
  const [planId, setPlanId] = useState("");
  const [force, setForce] = useState(false);
  const chosen = choices.find((p) => p.id === planId);
  const facilities = entitlements.limits.find((l) => l.code === "max_facilities");
  const facilityLimit = chosen?.limits.find((l) => l.limit_code === "max_facilities")?.limit_value;
  const usageNow = facilities?.usage?.value;
  const shortfall =
    facilityLimit !== undefined && usageNow !== undefined && facilityLimit < usageNow
      ? { limit: facilityLimit, usage: usageNow }
      : null;
  const below = shortfall !== null;

  const move = useMutation({
    mutationFn: () => {
      const body = { plan_id: planId, ...(force ? { force: true } : {}) };
      return withHeldKey(heldKey, { plan: tenant.id, body }, (key) =>
        updateTenant(tenant.id, body, key),
      );
    },
    onSuccess: (next) => {
      queryClient.setQueryData(tenantQueryKey(tenant.id), next);
      void queryClient.invalidateQueries({ queryKey: entitlementsQueryKey(tenant.id) });
      void queryClient.invalidateQueries({ queryKey: ["platform", "tenants"] });
      void queryClient.invalidateQueries({ queryKey: ["platform", "plans"] });
      onClose();
    },
  });
  useSignInAgainOn401(move.error);
  const failure = move.isError ? planFailure(move.error) : null;
  const title = t("admin.entitlements.planDialogTitle", { name: tenant.name });
  return (
    <Modal
      label={title}
      heading={title}
      closeLabel={t("admin.plans.close")}
      onClose={onClose}
      pending={move.isPending}
      testId="admin-plan-dialog"
      className="admin-modal"
    >
      <form
        className="admin-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (planId !== "" && (!below || force)) move.mutate();
        }}
      >
        {plans.isPending ? (
          <CardSkeleton lines={2} label={t("admin.plans.loading")} />
        ) : plans.isError ? (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void plans.refetch();
            }}
            requestId={requestIdOf(plans.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        ) : choices.length === 0 ? (
          <p className="admin-muted" data-testid="admin-plan-dialog-none">
            {t("admin.entitlements.noOtherPlan")}
          </p>
        ) : (
          <Field label={t("admin.entitlements.planSelect")}>
            <Select
              value={planId}
              data-testid="admin-plan-dialog-select"
              onChange={(e) => {
                setPlanId(e.target.value);
                setForce(false);
              }}
            >
              <option value="">{t("admin.entitlements.planPick")}</option>
              {choices.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {shortfall !== null ? (
          <>
            <Banner kind="warn" data-testid="admin-plan-dialog-below">
              {t("admin.entitlements.belowUsage", {
                limit: formatCount(shortfall.limit, i18n.language),
                usage: formatCount(shortfall.usage, i18n.language),
              })}
            </Banner>
            <CheckboxField
              label={t("admin.entitlements.force")}
              checked={force}
              onChange={setForce}
              data-testid="admin-plan-dialog-force"
            />
          </>
        ) : null}
        {failure !== null ? (
          <Banner kind="bad" data-testid="admin-plan-dialog-error">
            {failure === "below_usage"
              ? t("admin.entitlements.belowUsageRefused")
              : failure === "sandbox_only"
                ? t("admin.entitlements.sandboxOnly")
                : failure === "pack"
                  ? t("admin.entitlements.packNotInPlan")
                  : failure === "retired"
                    ? t("admin.plans.retiredEdit")
                    : failure === "role" || failure === "scope"
                      ? t("admin.tenant.refusedRole")
                      : t("admin.entitlements.planFailed")}
          </Banner>
        ) : null}
        <div className="admin-actions">
          <Button
            type="submit"
            variant="primary"
            disabled={planId === "" || (below && !force)}
            busy={move.isPending}
            busyLabel={t("admin.plans.saving")}
            data-testid="admin-plan-dialog-confirm"
          >
            {t("admin.entitlements.changePlan")}
          </Button>
          <Button disabled={move.isPending} onClick={onClose}>
            {t("admin.plans.cancel")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
